import { useMemo } from "react";
import { type AnyZodObject, z } from "zod";
import { createFieldValidation } from "@/components/site-builder/modules/CustomFormModule/utils/createFieldValidation";
import { generateFieldName } from "@/components/site-builder/modules/CustomFormModule/utils/generateFieldName";
import { getFormFields } from "@/components/site-builder/modules/CustomFormModule/utils/getFormFields";
import { useTranslation } from "@/i18n/client";
import { type FormBuilderFormEntity } from "@/types/form-builder";

export interface UseCustomFormValidationSchemaProps {
  formEntity: FormBuilderFormEntity;
  externalSchemas: Array<AnyZodObject | null>;
}

export function useCustomFormValidationSchema(
  props: UseCustomFormValidationSchemaProps,
) {
  const { formEntity, externalSchemas = [] } = props;
  const { t } = useTranslation("form-elements");

  return useMemo(() => {
    const fields = getFormFields(formEntity);
    const schemaFields: Record<string, z.ZodTypeAny> = {};

    fields.forEach((field) => {
      const fieldKey = generateFieldName(field);
      schemaFields[fieldKey] = createFieldValidation(field, t);
    });

    let validationSchema = z.object(schemaFields);

    if (externalSchemas.length > 0) {
      externalSchemas.forEach((externalSchema) => {
        if (externalSchema) {
          validationSchema = validationSchema.merge(externalSchema);
        }
      });
    }

    return validationSchema;
  }, [formEntity, externalSchemas, t]);
}
